import React, {useCallback, useState} from 'react';
import useResizeObserver from 'use-resize-observer';

import {type MediaSize} from '../../ImageCard/resolveMediaSize';
import {getKeyForTileMediaSpec} from '../fetch/keys';
import {containFit} from '../sizing/containFit';
import {TileMediaLoader} from '../TileMediaLoader';

type Props = React.ComponentProps<typeof TileMediaLoader>;

const isUsable = (size: {width?: number; height?: number}) =>
  Number.isFinite(size.width) &&
  Number.isFinite(size.height) &&
  (size.width as number) > 0 &&
  (size.height as number) > 0;

/**
 * Fits the ordinary tile image inside the flex area remaining after tile
 * headers and captions. Layout card dimensions are estimates; the image's
 * logged history size is used until the loaded image reports its resolved
 * size through onImageRender, mirroring how the fullscreen modal sizes.
 */
export const CompareFitTile: React.FC<Props> = props => {
  const {ref: viewportRef, width, height} = useResizeObserver<HTMLDivElement>();
  const specKey = getKeyForTileMediaSpec(props.tileMediaSpec);
  const [reported, setReported] = useState<{
    key: string;
    size: MediaSize;
  } | null>(null);
  const onImageRender = useCallback(
    (size: MediaSize) => setReported({key: specKey, size}),
    [specKey]
  );

  // A size reported by the previous media never sizes its replacement.
  const resolved = reported?.key === specKey ? reported.size : null;
  const history = {width: props.mediaWidth, height: props.mediaHeight};
  const dims = resolved ?? (isUsable(history) ? (history as MediaSize) : null);
  const container = {
    width: width ?? props.width,
    height: height ?? props.height,
  };
  const cardSize =
    dims != null && isUsable(container)
      ? containFit(dims, container)
      : {width: props.width, height: props.height};

  return (
    <div
      ref={viewportRef}
      data-test="compare-tile-viewport"
      className="flex w-full h-full min-w-0 min-h-0 overflow-hidden justify-center items-center">
      <TileMediaLoader
        {...props}
        width={Math.max(cardSize.width, 1)}
        height={Math.max(cardSize.height, 1)}
        onImageRender={onImageRender}
      />
    </div>
  );
};
